import { Component, OnInit } from '@angular/core';
import { BreadcrumbComponent } from "@shared/components/breadcrumb/breadcrumb.component";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButton } from "@angular/material/button";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { PrescriptionService } from "@core/http/table-data-services/prescription.service";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { SelectionModel } from "@angular/cdk/collections";
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable
} from "@angular/material/table";
import { MatRipple } from "@angular/material/core";
import { MatSort, MatSortHeader } from "@angular/material/sort";
import { MatTooltip } from "@angular/material/tooltip";
import { MatDivider } from "@angular/material/divider";
import { NgClass, NgIf } from "@angular/common";
import { IDetalleReceta } from "@core/interfaces/receta-data";
import { RecetaCompletaService } from "@core/http/table-data-services/receta-completa.service";
import Swal from "sweetalert2";
import { MatChipsModule } from "@angular/material/chips";
import { ZXingScannerModule } from "@zxing/ngx-scanner";
import { BarcodeFormat, BrowserMultiFormatReader } from "@zxing/library";
import { HttpMesageHandlerService } from "@core/service/http-mesage-handler";
import { HttpErrorResponse } from "@angular/common/http";
import { MsgResponse } from "@core/models/MsgResponse";
import { MatCard, MatCardContent, MatCardHeader, MatCardTitle } from "@angular/material/card";
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelDescription,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from "@angular/material/expansion";
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIcon, MatIconModule } from "@angular/material/icon";
import { MatDialog } from "@angular/material/dialog";

// ✅ Modal PIN (el mismo que usas en perfil)
import { PatientPinDialogComponent } from '@shared/components/patient-pin-dialog/patient-pin-dialog.component';

@Component({
  selector: 'app-prescription-supply',
  standalone: true,
  imports: [
    BreadcrumbComponent,
    FormsModule,
    MatButton,
    MatFormField,
    MatInput,
    MatLabel,
    MatDivider,
    ReactiveFormsModule,
    MatTooltip,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatHeaderCellDef,
    MatCellDef,
    MatHeaderRow,
    MatRow,
    MatHeaderRowDef,
    MatRowDef,
    MatRipple,
    MatSort,
    NgClass,
    MatSortHeader,
    MatChipsModule,
    ZXingScannerModule,
    NgIf,
    MatCard,
    MatCardContent,
    MatCardTitle,
    MatCardHeader,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    MatSlideToggleModule,
    MatIcon,
    MatIconModule,
  ],
  templateUrl: './prescription-supply.component.html',
  styleUrl: './prescription-supply.component.scss'
})
export class PrescriptionSupplyComponent implements OnInit {
  searchPrescription: string = '';
  detalle: any;

  // ===== QR =====
  showScanner: boolean = false;
  allowedFormats = [BarcodeFormat.QR_CODE];
  hasCamera: boolean = false;
  cameraChecked: boolean = false;

  // ===== OCULTOS / PIN (modal) =====
  showHiddenPinBox: boolean = false; // solo controla el "estado visual" del chip (opcional)
  private lastSearchValue: string = ''; // folio o QR que se buscó por última vez

  detalleDataSource: BehaviorSubject<any[]> = new BehaviorSubject<IDetalleReceta[]>([]);
  selection = new SelectionModel<IDetalleReceta>(true, []);
  displayedColumns = ['nombre', 'piezas', 'cantidadSurtida', 'acciones'];

  constructor(
    private prescriptionService: PrescriptionService,
    private recetaCompletaService: RecetaCompletaService,
    private httpMsgHandler: HttpMesageHandlerService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.detalleDataSource = this.recetaCompletaService.dataChange;
    this.recetaCompletaService.resetDataSource();

    const codeReader = new BrowserMultiFormatReader();
    codeReader.listVideoInputDevices()
      .then((videoInputDevices) => {
        this.hasCamera = videoInputDevices.length > 0;
        this.cameraChecked = true;

        if (!this.hasCamera) {
          Swal.fire({
            icon: 'warning',
            title: 'Sin camara detectada',
            text: 'Este dispositivo no tiene camara disponible. Por favor intenta en un equipo con camara.',
            confirmButtonText: 'Cerrar'
          });
        }
      })
      .catch((error) => {
        this.hasCamera = false;
        this.cameraChecked = true;
      });
  }

  // =========================================================
  // ✅ OCULTOS: ABRIR MODAL PIN (como perfil)
  // =========================================================
  async openHiddenPinModal(): Promise<void> {
    // si no hay última búsqueda, no hay nada que re-consultar
    if (!this.lastSearchValue) {
      Swal.fire({
        icon: 'info',
        title: 'BUSCA UNA RECETA',
        text: 'PRIMERO CAPTURA EL FOLIO O ESCANEA EL QR.',
        confirmButtonText: 'OK'
      }).then();
      return;
    }

    const dialogRef = this.dialog.open(PatientPinDialogComponent, {
      disableClose: false,
      autoFocus: false,
      width: '560px',
      maxWidth: '92vw',
      panelClass: ['rmd-pin-dialog-panel'],
      data: {
        mode: 'ADMIN', // PV normalmente es staff; si tienes roles aquí lo ajustamos
        title: 'MEDICAMENTOS PROTEGIDOS',
        message: 'LA RECETA TIENE MEDICAMENTOS OCULTOS. INGRESA EL PIN PARA VISUALIZARLOS.',
        optional: true, // permite continuar sin pin
      }
    });

    const pin = await firstValueFrom(dialogRef.afterClosed());

    // cerró el diálogo
    if (pin === undefined as any) return;

    // si viene vacío => recarga sin pin (opcional)
    if (!pin) {
      this.doSearch(null);
      return;
    }

    // con pin
    this.doSearch(String(pin));
  }

  // =========================================================
  // ====== CAMARA ======
  // =========================================================
  handlePermissionException(error: any): void {
    this.showScanner = false;

    if (!this.hasCamera) {
      Swal.fire({
        icon: 'warning',
        title: 'Sin camara detectada',
        text: 'Este dispositivo no tiene camara disponible. Por favor intenta en un equipo con camara.',
        confirmButtonText: 'Cerrar'
      });
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Error con la camara',
        text: 'La camara esta siendo utilizada por otra aplicacion o los permisos estan bloqueados.',
        confirmButtonText: 'Cerrar'
      });
    }

  }

  onHasPermission(hasPermission: boolean): void {
    if (!hasPermission && this.hasCamera) {
      this.showScanner = false;
      Swal.fire({
        icon: 'error',
        title: 'Permiso denegado',
        text: 'La aplicacion no tiene permiso para acceder a la camara.',
        confirmButtonText: 'Cerrar'
      });
    }
  }

  toggleScanner(): void {
    if (!this.hasCamera) {
      Swal.fire({
        icon: 'warning',
        title: 'Sin camara detectada',
        text: 'Este dispositivo no tiene camara disponible. Por favor intenta en un equipo con camara.',
        confirmButtonText: 'Cerrar'
      });
      return;
    }
    this.showScanner = !this.showScanner;
  }

  handleQrCodeResult(resultString: string): void {
    this.searchPrescription = resultString;
    this.lastSearchValue = resultString; // ✅ para reintento con PIN
    this.showScanner = false;
    this.doSearch();
  }

  // ====== INPUTS ======
  preventInvalidInput(event: KeyboardEvent) {
    if (["e", "E", "+", "-"].includes(event.key)) {
      event.preventDefault();
    }
  }

  onCantidadSurtidaChange(row: any) {
    if (row.cantidadSurtida > row.piezas) {
      row.cantidadSurtida = row.piezas;
      Swal.fire({
        icon: 'warning',
        title: 'Cantidad excedida',
        text: `No puedes surtir mas de ${row.piezas} piezas.`,
        confirmButtonText: 'Ok',
        confirmButtonColor: '#00a5b8'
      }).then();
    } else if (row.cantidadSurtida < 0) {
      row.cantidadSurtida = 0;
    }
  }

  onToggleFull(row: any, isOn: boolean) {
    if (isOn) {
      this.selection.select(row);
      row.cantidadSurtida = row.piezas;
    } else {
      this.selection.deselect(row);
    }
  }

  // =========================================================
  // BUSQUEDA (folio o QR) + PIN OPCIONAL
  // =========================================================
  doSearch(pin: string | null = null) {
    this.recetaCompletaService.resetDataSource();
    this.selection.clear();

    // si viene pin => reutiliza lastSearchValue
    const raw = (pin ? this.lastSearchValue : this.searchPrescription).toString().trim();

    if (!raw) {
      Swal.fire({
        icon: 'info',
        title: 'FOLIO REQUERIDO',
        text: 'CAPTURA EL FOLIO O ESCANEA EL QR.',
        confirmButtonText: 'OK'
      }).then();
      return;
    }

    // Guardamos para reintentar con PIN
    this.lastSearchValue = raw;

    const isQr = raw.length > 50;

    const obs$ = isQr
      ? this.prescriptionService.getPrescriptionByQr(raw, pin)
      : this.prescriptionService.getPrescriptionById(raw, pin);

    obs$.subscribe({
      next: (data: any) => {
        this.detalle = data?.data?.receta ?? null;

        const detalles = data?.data?.detalles ?? [];
        detalles.forEach((value: any) => this.addNewLine(value));

        if (detalles.length < 1) {
          this.httpMsgHandler.handleMsg(data as MsgResponse);
        }

        // Limpio el input visible
        this.searchPrescription = '';
      },
      error: (err: HttpErrorResponse) => {
        this.httpMsgHandler.handleMsg(err);

        // si fue búsqueda normal, limpia input
        if (!pin) this.searchPrescription = '';
      }
    });
  }

  traducirUnidadDuracion(duracion: number, unidad: string): string {
    switch (unidad) {
      case 'DAY': return duracion === 1 ? 'DIA' : 'DIAS';
      case 'WEEK': return duracion === 1 ? 'SEMANA' : 'SEMANAS';
      case 'MONTH': return duracion === 1 ? 'MES' : 'MESES';
      case 'YEAR': return duracion === 1 ? 'AÑO' : 'AÑOS';
      case 'MINUTES': return duracion === 1 ? 'MINUTO' : 'MINUTOS';
      case 'HOURS': return duracion === 1 ? 'HORA' : 'HORAS';
      case 'SECONDS': return duracion === 1 ? 'SEGUNDO' : 'SEGUNDOS';
      default: return unidad;
    }
  }

  addNewLine(value: any): void {
    if (value.cantidadSurtida == null) {
      value.cantidadSurtida = 0;
    }
    this.selection.deselect(value);
    this.recetaCompletaService.addRecetaLine(value);
  }

  canConfirm(): boolean {
    const rows = this.detalleDataSource.getValue() || [];
    return rows.some(r => this.selection.isSelected(r) || Number(r.cantidadSurtida) > 0);
  }

  surtir() {
    const detallesReceta: any[] = [];
    const rows = this.detalleDataSource.getValue() || [];

    rows.forEach((row) => {
      if (this.selection.isSelected(row)) {
        detallesReceta.push({
          idDetalleReceta: row.idDetalleReceta,
          medicamentoId: row.medicamentoId,
          piezasSurtidas: row.piezas
        });
      } else if (Number(row.cantidadSurtida) > 0) {
        detallesReceta.push({
          idDetalleReceta: row.idDetalleReceta,
          medicamentoId: row.medicamentoId,
          piezasSurtidas: Math.min(Number(row.cantidadSurtida), row.piezas)
        });
      }
    });

    if (detallesReceta.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Nada para surtir',
        text: 'Activa el switch “Completo” o captura cantidades parciales.'
      });
      return;
    }

    const full = {
      idReceta: this.detalle.idReceta,
      detallesReceta
    };

    this.recetaCompletaService.postSurtir(full).subscribe({
      next: (res) => {
        const code = res.code || 'CODIGO NO DISPONIBLE';
        const message = res.message || 'DESCRIPCION DESCONOCIDA';
        Swal.fire({
          icon: 'success',
          title: '¡RECETA SURTIDA CORRECTAMENTE!',
          text: message,
          footer: `CODIGO: (${code})`,
        }).then(() => {
          this.clearAfterSurtir();
        });
      },
      error: (err: HttpErrorResponse) => {
        this.httpMsgHandler.handleMsg(err);
      }
    });
  }

  private clearAfterSurtir(): void {
    this.searchPrescription = '';
    this.detalle = null;
    this.recetaCompletaService.resetDataSource();
    this.selection.clear();
    this.showScanner = false;

    // ocultos
    this.lastSearchValue = '';
  }
}
